/**
 * Snaply's — Free Shipping Bar (custom, no app)
 * Fixed round thresholds per zone (EUR 15 / USD 15 / GBP 12), while shoppers
 * keep browsing and paying in their own local currency.
 * Config comes from sections/free-shipping-bar.liquid (#SnaplysFsbConfig).
 */
(() => {
  if (window.__snaplysFsbLoaded) return;
  window.__snaplysFsbLoaded = true;

  const DEFAULTS = {
    country: (window.Shopify && Shopify.country) || '',
    shopCurrency: 'EUR',
    thresholds: { EUR: 15, USD: 15, GBP: 12 },
    euCountries: 'AT,BE,BG,HR,CY,CZ,DK,EE,FI,FR,DE,GR,HU,IE,IT,LV,LT,LU,MT,NL,PL,PT,RO,SK,SI,ES,SE',
    gbpCountries: 'GB',
    plainUsdCountries: 'US',
    marginPercent: 1,
    show: { top: true, drawer: true, cart: true, product: true },
    text: {
      empty: '🌍 FREE worldwide shipping on orders over [threshold]!',
      progress: "You're [amount] away from free shipping!",
      success: "🎉 You've unlocked FREE Shipping! 🎉",
    },
    cart: null,
  };

  const FX_URL = 'https://cdn.shopify.com/s/javascripts/currencies.js';
  const FX_CACHE_KEY = 'snaplysFsbFx';
  const FX_TTL = 6 * 60 * 60 * 1000;

  const instances = new Set();
  let config = readConfig();
  const state = {
    total: config.cart ? Number(config.cart.total) || 0 : null,
    count: config.cart ? Number(config.cart.count) || 0 : null,
    currency: (config.cart && config.cart.currency) || activeCurrency(),
    fx: null,
    fxFailed: false,
  };
  let wasUnlocked = null;

  function readConfig() {
    let parsed = {};
    const el = document.getElementById('SnaplysFsbConfig');
    if (el) {
      try { parsed = JSON.parse(el.textContent); } catch (e) { parsed = {}; }
    }
    const cfg = Object.assign({}, DEFAULTS, parsed);
    cfg.thresholds = Object.assign({}, DEFAULTS.thresholds, parsed.thresholds || {});
    cfg.show = Object.assign({}, DEFAULTS.show, parsed.show || {});
    cfg.text = Object.assign({}, DEFAULTS.text, parsed.text || {});
    for (const k of Object.keys(DEFAULTS.text)) if (!cfg.text[k]) cfg.text[k] = DEFAULTS.text[k];
    cfg.eu = toList(cfg.euCountries);
    cfg.gbp = toList(cfg.gbpCountries);
    cfg.plainUsd = toList(cfg.plainUsdCountries);
    cfg.country = String(cfg.country || DEFAULTS.country || '').toUpperCase();
    return cfg;
  }

  function toList(value) {
    return String(value || '').toUpperCase().split(/[\s,;]+/).filter(Boolean);
  }

  function activeCurrency() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || config.shopCurrency;
  }

  function zone() {
    const c = config.country;
    if (config.gbp.includes(c)) return { currency: 'GBP', min: Number(config.thresholds.GBP) };
    if (config.eu.includes(c)) return { currency: 'EUR', min: Number(config.thresholds.EUR) };
    return { currency: 'USD', min: Number(config.thresholds.USD) };
  }

  /* ---------- currency helpers ---------- */

  function loadFx() {
    if (state.fx || state.fxFailed || loadFx.pending) return loadFx.pending || Promise.resolve();
    try {
      const cached = JSON.parse(sessionStorage.getItem(FX_CACHE_KEY) || 'null');
      if (cached && Date.now() - cached.t < FX_TTL && cached.rates) {
        state.fx = cached.rates;
        return Promise.resolve();
      }
    } catch (e) { /* storage unavailable */ }
    loadFx.pending = nativeFetch(FX_URL)
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((txt) => {
        const m = txt.match(/rates\s*:\s*\{([^}]*)\}/);
        if (!m) throw new Error('no rates');
        const rates = {};
        m[1].split(',').forEach((pair) => {
          const [k, v] = pair.split(':');
          const n = Number(v);
          if (k && isFinite(n) && n > 0) rates[k.trim()] = n;
        });
        if (!rates.USD || !rates.EUR) throw new Error('incomplete rates');
        state.fx = rates;
        try { sessionStorage.setItem(FX_CACHE_KEY, JSON.stringify({ t: Date.now(), rates })); } catch (e) {}
      })
      .catch(() => { state.fxFailed = true; })
      .finally(() => { loadFx.pending = null; renderAll(); });
    return loadFx.pending;
  }

  /** Converts the cart total (major units, cart currency) into the zone currency. */
  function convert(amount, from, to) {
    if (from === to) return { value: amount, exact: true };
    const shop = config.shopCurrency;
    const shopRate = window.Shopify && Shopify.currency && Shopify.currency.active === from
      ? Number(Shopify.currency.rate) : NaN;
    // Leg 1: cart currency -> shop currency (Shopify's own storefront rate)
    let inShop = null;
    if (from === shop) inShop = amount;
    else if (shopRate > 0) inShop = amount / shopRate;
    if (to === shop && inShop !== null) return { value: inShop, exact: false, ownRate: true };
    // Leg 2: needs the FX table
    if (!state.fx) { loadFx(); return null; }
    const fx = state.fx;
    if (inShop !== null && fx[shop] && fx[to]) return { value: (inShop * fx[shop]) / fx[to], exact: false };
    if (fx[from] && fx[to]) return { value: (amount * fx[from]) / fx[to], exact: false };
    return null;
  }

  function fmt(value, currency, forceDecimals) {
    const whole = Math.abs(value - Math.round(value)) < 0.005;
    let out;
    try {
      out = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: whole && !forceDecimals ? 0 : 2,
        maximumFractionDigits: whole && !forceDecimals ? 0 : 2,
      }).format(value);
    } catch (e) {
      out = `${value.toFixed(2)} ${currency}`;
    }
    if (currency === 'USD' && !config.plainUsd.includes(config.country)) out += ' USD';
    return out;
  }

  /* ---------- view model ---------- */

  function viewModel() {
    const z = zone();
    const threshold = fmt(z.min, z.currency, false);
    if (state.total === null) return { state: 'empty', html: tpl(config.text.empty, { threshold }), pct: 0 };
    if (!state.count || state.total <= 0) {
      return { state: 'empty', html: tpl(config.text.empty, { threshold }), pct: 0 };
    }
    const conv = convert(state.total / 100, state.currency, z.currency);
    if (!conv) {
      return { state: state.fxFailed ? 'info' : 'loading', html: tpl(config.text.empty, { threshold }), pct: 0 };
    }
    const margin = conv.exact || conv.ownRate ? 0 : Math.max(0, Number(config.marginPercent) || 0) / 100;
    const target = z.min * (1 + margin);
    if (conv.exact) {
      const remainingCents = Math.round(z.min * 100) - state.total;
      if (remainingCents <= 0) return { state: 'unlocked', html: tpl(config.text.success, { threshold }), pct: 100 };
      return {
        state: 'progress',
        html: tpl(config.text.progress, { threshold, amount: fmt(remainingCents / 100, z.currency, true) }),
        pct: Math.max(2, Math.min(100, (state.total / (z.min * 100)) * 100)),
      };
    }
    if (conv.value + 1e-6 >= target) return { state: 'unlocked', html: tpl(config.text.success, { threshold }), pct: 100 };
    const remaining = Math.max(0.01, Math.ceil((target - conv.value) * 100) / 100);
    return {
      state: 'progress',
      html: tpl(config.text.progress, { threshold, amount: fmt(remaining, z.currency, true) }),
      pct: Math.max(2, Math.min(99, (conv.value / target) * 100)),
    };
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  function tpl(text, vars) {
    return esc(text)
      .replace(/\[threshold\]/gi, `<strong>${esc(vars.threshold || '')}</strong>`)
      .replace(/\[amount\]/gi, `<strong>${esc(vars.amount || '')}</strong>`);
  }

  /* ---------- custom element ---------- */

  const STYLE = `
    :host{display:block;color:var(--fsb-text,#F5F5F5);font:inherit}
    :host([hidden]){display:none!important}
    :host([data-context="product"]){flex:1 0 100%;width:100%;margin-block-start:4px}
    .fsb{box-sizing:border-box;display:grid;gap:9px;padding:12px 16px;border-radius:12px;text-align:center;
      background:var(--fsb-bg-alt,#1E1E1C);color:var(--fsb-text,#F5F5F5)}
    :host([data-context="top"]) .fsb{border-radius:0;padding:8px 16px 10px;gap:6px;background:var(--fsb-bg,#1A1A1A)}
    .msg{margin:0;font-size:14px;line-height:1.35;letter-spacing:.01em;text-wrap:balance}
    :host([data-context="top"]) .msg{font-size:13px}
    .msg strong{font-weight:700}
    .track{height:6px;border-radius:999px;overflow:hidden;background:rgba(245,245,245,.16);
      background:color-mix(in srgb,var(--fsb-bar,#F5F5F5) 16%,transparent)}
    :host([data-context="top"]) .track{height:4px;width:min(340px,85%);margin-inline:auto}
    .fill{height:100%;width:0;border-radius:inherit;background:var(--fsb-bar,#F5F5F5);
      transition:width .5s cubic-bezier(.2,.8,.2,1),background-color .3s ease}
    .fsb[data-state="unlocked"] .fill{background:var(--fsb-success,#4ADE80)}
    .fsb[data-state="unlocked"] .msg{color:var(--fsb-success-text,#F5F5F5)}
    .fsb[data-state="info"] .track,.fsb[data-state="loading"] .track{visibility:hidden}
    .fsb[data-state="info"] .track{display:none}
    .fsb.pop .msg{animation:fsb-pop .6s ease}
    @keyframes fsb-pop{0%{transform:scale(1)}40%{transform:scale(1.06)}100%{transform:scale(1)}}
    @media (prefers-reduced-motion:reduce){.fill{transition:none}.fsb.pop .msg{animation:none}}
  `;

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      if (!this.shadowRoot) {
        const root = this.attachShadow({ mode: 'open' });
        root.innerHTML = `<style>${STYLE}</style>
          <div class="fsb" part="bar" data-state="empty">
            <p class="msg" aria-live="polite"></p>
            <div class="track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-label="Free shipping progress">
              <div class="fill"></div>
            </div>
          </div>`;
      }
      instances.add(this);
      this.render(viewModel(), false);
    }

    disconnectedCallback() {
      instances.delete(this);
    }

    render(vm, pop) {
      const ctx = this.dataset.context || 'top';
      this.hidden = config.show[ctx] === false;
      const root = this.shadowRoot;
      const box = root.querySelector('.fsb');
      const msg = root.querySelector('.msg');
      const track = root.querySelector('.track');
      if (this._html !== vm.html) {
        msg.innerHTML = vm.html;
        this._html = vm.html;
      }
      box.dataset.state = vm.state;
      root.querySelector('.fill').style.width = `${vm.pct}%`;
      track.setAttribute('aria-valuenow', String(Math.round(vm.pct)));
      if (pop) {
        box.classList.remove('pop');
        void box.offsetWidth;
        box.classList.add('pop');
      }
    }
  }

  function renderAll() {
    const vm = viewModel();
    const unlocked = vm.state === 'unlocked';
    const pop = wasUnlocked === false && unlocked;
    if (vm.state !== 'loading' || wasUnlocked === null) wasUnlocked = unlocked;
    instances.forEach((el) => el.render(vm, pop));
  }

  /* ---------- cart syncing ---------- */

  const nativeFetch = window.fetch.bind(window);
  let refreshTimer = null;

  function refreshCart(delay) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      nativeFetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
        .then((r) => r.json())
        .then((cart) => {
          state.total = Number(cart.total_price) || 0;
          state.count = Number(cart.item_count) || 0;
          state.currency = cart.currency || activeCurrency();
          renderAll();
        })
        .catch(() => {});
    }, delay == null ? 150 : delay);
  }

  function isCartMutation(url, method, body) {
    const u = String(url || '');
    const m = String(method || 'GET').toUpperCase();
    if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(u)) return true;
    if (m !== 'GET' && /\/cart(\.js)?(\?|$)/.test(u)) return true;
    if (m !== 'GET' && /graphql/.test(u) && typeof body === 'string' && /cart/i.test(body)) return true;
    return false;
  }

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const method = (init && init.method) || (input && input.method) || 'GET';
    const body = init && init.body;
    const p = nativeFetch(input, init);
    if (isCartMutation(url, method, body)) p.then(() => refreshCart(), () => refreshCart());
    return p;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__fsb = { method, url };
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    const info = this.__fsb;
    if (info && isCartMutation(info.url, info.method, body)) {
      this.addEventListener('loadend', () => refreshCart());
    }
    return xhrSend.apply(this, arguments);
  };

  ['cart:update', 'cart:updated', 'cart:refresh', 'cart:change', 'shopify:cart:update'].forEach((evt) =>
    document.addEventListener(evt, () => refreshCart())
  );
  window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCart(0); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshCart(0); });

  // Theme editor: re-read settings when the section is edited
  document.addEventListener('shopify:section:load', () => {
    config = readConfig();
    renderAll();
  });

  // Small debug handle (read-only use): window.SnaplysFSB.viewModel()
  window.SnaplysFSB = {
    state,
    viewModel,
    refresh: () => refreshCart(0),
    get config() { return config; },
    _setConfig(patch) { config = Object.assign(config, patch); renderAll(); },
  };

  customElements.define('free-shipping-bar', FreeShippingBar);
  if (state.currency !== zone().currency && config.shopCurrency !== zone().currency) loadFx();
  refreshCart(0);
})();
